import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Copy, CheckCircle2, ArrowLeft, QrCode } from "lucide-react";

export default function PayPix() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [senderName, setSenderName] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/payments/pix/info").then(({data}) => setInfo(data));
  }, [user, loading, nav]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  const confirm = async () => {
    setSending(true);
    try {
      await api.post("/payments/pix/confirm", { sender_name: senderName });
      await refresh();
      setConfirmed(true);
      toast.success("Confirmação recebida! Aguarda aprovação do admin.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao confirmar");
    } finally { setSending(false); }
  };

  if (!user || !info) return <div className="p-10 text-white/40">Carregando...</div>;

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(info.brcode)}`;

  if (confirmed) {
    return (
      <div data-testid="pix-confirmed" className="grid-bg min-h-[85vh] flex items-center justify-center px-4 py-16">
        <div className="brutal-card brutal-shadow max-w-md text-center" style={{borderColor:'#FFCE00'}}>
          <CheckCircle2 className="mx-auto text-[#FFCE00]" size={64}/>
          <div className="font-bebas text-5xl mt-4 tracking-tighter">RECEBIDO ✅</div>
          <p className="mt-3 text-white/70 font-manrope">
            Sua confirmação chegou pro admin. Assim que o pagamento for aprovado (geralmente em poucas horas),
            sua assinatura é liberada por 30 dias e você recebe um aviso por email.
          </p>
          <p className="mt-3 text-white/50 font-manrope text-sm">
            Se já faz mais de 24h e não liberou, fala com o suporte.
          </p>
          <Link to="/cursos" data-testid="confirmed-to-dashboard" className="brutal-btn mt-8 w-full">Ver aula grátis</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pay-pix-page" className="grid-bg min-h-[85vh]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <Link to="/cursos" data-testid="pix-back" className="inline-flex items-center gap-2 text-white/60 hover:text-[#CCFF00] font-manrope text-sm uppercase tracking-widest">
          <ArrowLeft size={16}/> Voltar
        </Link>

        <div className="mt-6 flex items-center gap-3">
          <QrCode className="text-[#CCFF00]" size={40}/>
          <span className="sticker">PAGAMENTO PIX</span>
        </div>
        <h1 className="mt-6 font-bebas text-6xl md:text-7xl tracking-tighter">
          PAGA E <span className="text-[#CCFF00]">LIBERA</span>
        </h1>
        <p className="text-white/60 mt-3 font-manrope max-w-xl">
          Faz um Pix de <b className="text-white">R$ {info.amount.toFixed(2).replace('.',',')}</b> pra chave abaixo. Depois clica em "Já enviei o Pix" e tua assinatura é liberada por 30 dias assim que a gente conferir o pagamento.
        </p>

        <div className="mt-10 grid md:grid-cols-12 gap-6">
          {/* QR Code */}
          <div className="md:col-span-5 brutal-card text-center" style={{borderColor:'#CCFF00'}}>
            <div className="font-bebas text-xs tracking-widest text-white/50">ESCANEIA O QR</div>
            <div className="mt-4 p-4 bg-white inline-block">
              <img src={qrUrl} alt="QR Code Pix" data-testid="pix-qr" className="w-64 h-64"/>
            </div>
            <div className="mt-4 font-manrope text-xs text-white/50">Abra o app do banco → Pix → Ler QR Code</div>
          </div>

          {/* Details */}
          <div className="md:col-span-7 space-y-5">
            <div className="brutal-card">
              <div className="font-manrope text-xs uppercase tracking-widest text-white/50">
                Chave Pix ({info.pix_key_type === "aleatoria" ? "Aleatória" : info.pix_key_type === "email" ? "E-mail" : info.pix_key_type === "telefone" ? "Telefone" : info.pix_key_type === "cpf_cnpj" ? "CPF/CNPJ" : "Chave"})
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div data-testid="pix-key" className="font-mono text-lg text-[#CCFF00] break-all flex-1">{info.pix_key}</div>
                <button data-testid="pix-copy-key" onClick={()=>copy(info.pix_key, "Chave")} className="brutal-btn px-3 py-2 text-sm"><Copy size={16}/></button>
              </div>
            </div>

            <div className="brutal-card">
              <div className="font-manrope text-xs uppercase tracking-widest text-white/50">Valor</div>
              <div className="mt-2 font-bebas text-4xl text-white">R$ {info.amount.toFixed(2).replace('.',',')}</div>
            </div>

            <div className="brutal-card">
              <div className="font-manrope text-xs uppercase tracking-widest text-white/50">Pix Copia e Cola</div>
              <div className="mt-2 flex gap-3">
                <div data-testid="pix-brcode" className="font-mono text-xs text-white/70 break-all flex-1 max-h-24 overflow-y-auto">{info.brcode}</div>
                <button data-testid="pix-copy-brcode" onClick={()=>copy(info.brcode, "Código Pix")} className="brutal-btn px-3 py-2 text-sm h-fit"><Copy size={16}/></button>
              </div>
            </div>

            <div className="brutal-card" style={{borderColor:'#00F0FF'}}>
              <div className="font-bebas text-xl tracking-tight text-[#00F0FF]">Depois de pagar</div>
              <p className="text-white/70 font-manrope text-sm mt-2">Preenche teu nome (igual ao do Pix) e clica no botão. A gente confere o pagamento e libera a assinatura por 30 dias.</p>
              <input
                data-testid="pix-sender-name"
                value={senderName}
                onChange={e=>setSenderName(e.target.value)}
                placeholder="Nome do titular do Pix"
                className="brutal-input mt-4"
              />
              <button
                data-testid="pix-confirm"
                onClick={confirm}
                disabled={sending || !senderName}
                className="brutal-btn w-full mt-4"
              >
                {sending ? "Confirmando..." : "Já enviei o Pix"}
              </button>
              <p className="text-white/40 font-manrope text-xs mt-3">
                * Ao confirmar, você declara que fez o Pix. Pagamentos não localizados podem ter a assinatura desativada.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
